// Force rebuild v4 - Payment fix
import { useState, useEffect } from 'react';
import { useAuth } from './contexts/AuthContext';
import Landing from './components/Landing';
import SubmitForm from './components/SubmitForm';
import VotingGallery from './components/VotingGallery';
import AuraFarming from './components/AuraFarming';
import Leaderboard from './components/Leaderboard';
import BuyVotes from './components/BuyVotes';
import UserProfile from './components/UserProfile';
import BoostShop from './components/BoostShop';
import SpotlightFeed from './components/SpotlightFeed';
import AuthCallback from './components/AuthCallback';
import LoginModal from './components/LoginModal';
import GuestBanner from './components/GuestBanner';
import Dashboard from './components/Dashboard';
import EmailVerification from './components/EmailVerification';
import ResetPasswordForm from './components/ResetPasswordForm';
import RobloxLinkCallback from './components/RobloxLinkCallback';
import RobloxLinkingPage from './components/RobloxLinkingPage';
import { getSession, initializeSession, addTokens, claimDailyTokens as claimTokensLocal, syncTokensWithServer } from './utils/session';
import { verifyPayment, checkVPN, claimDailyTokens, checkDailyClaimStatus } from './utils/api';
import { setupGlobalErrorHandlers } from './utils/errorLogger';

function App() {
  const { user, loading, logout, checkAuth } = useAuth();
  const [currentView, setCurrentView] = useState('landing');
  const [session, setSession] = useState(null);
  const [liveBalance, setLiveBalance] = useState(0); // Dedicated live balance state
  const [topAvatars, setTopAvatars] = useState([]);
  const [selectedUsername, setSelectedUsername] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState('');
  const [isVPN, setIsVPN] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dailyClaimStatus, setDailyClaimStatus] = useState('checking'); // 'checking', 'available', 'claimed', 'vpn_blocked'
  const [timeUntilReset, setTimeUntilReset] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    // Set up global error handlers
    setupGlobalErrorHandlers();

    // Initialize session on mount
    const sessionData = initializeSession();
    setSession(sessionData);
    setLiveBalance(sessionData?.tokens || 0);

    // Sync tokens with server
    syncServerBalance();

    // Fetch top 3 avatars for landing page
    fetchTop3Avatars();

    // Handle payment redirect
    handlePaymentRedirect();

    // Check for VPN
    checkForVPN();

    // Handle share link (e.g., /?share=username)
    const urlParams = new URLSearchParams(window.location.search);
    const shareUsername = urlParams.get('share') || urlParams.get('profile');
    if (shareUsername) {
      setSelectedUsername(shareUsername);
      setCurrentView('profile');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Countdown timer for daily reset (4pm UTC)
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const next4pm = new Date(now);

      // Set to 4pm UTC today
      next4pm.setUTCHours(16, 0, 0, 0);

      // If we're past 4pm UTC today, move to tomorrow 4pm UTC
      if (now >= next4pm) {
        next4pm.setUTCDate(next4pm.getUTCDate() + 1);
      }

      const diff = next4pm - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeUntilReset(`${hours}h ${minutes}m ${seconds}s`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const syncServerBalance = async (immediateBalance) => {
    console.log('🔄 [App] Syncing server balance...');

    // If balance is provided directly (from vote response), update immediately
    if (immediateBalance !== undefined) {
      setLiveBalance(immediateBalance);
      // Also update session state to trigger child component re-renders
      const sessionData = getSession();
      if (sessionData) {
        sessionData.tokens = immediateBalance;
        setSession({ ...sessionData });
      }
      console.log('💰 [App] Balance updated immediately:', immediateBalance);
      return;
    }

    // Otherwise check localStorage for immediate update
    const sessionData = getSession();
    if (sessionData && sessionData.tokens !== undefined) {
      setLiveBalance(sessionData.tokens);
      setSession({ ...sessionData });
      console.log('💰 [App] Balance updated from localStorage:', sessionData.tokens);
    }

    // Then sync with server in background for accuracy
    const balance = await syncTokensWithServer();
    console.log('💰 [App] Server returned balance:', balance);
    if (balance !== null) {
      setLiveBalance(balance); // Update live balance with server value
      updateSession();
      console.log('✅ [App] Session state updated, live balance:', balance);
    }
  };

  const checkForVPN = async () => {
    try {
      // Check daily claim status without claiming
      const claimStatus = await checkDailyClaimStatus();

      console.log('Claim status response:', claimStatus);

      if (claimStatus.status === 'vpn_blocked') {
        setIsVPN(true);
        setDailyClaimStatus('vpn_blocked');
      } else if (claimStatus.status === 'already_claimed') {
        setDailyClaimStatus('claimed');
      } else if (claimStatus.status === 'available' && claimStatus.canClaim) {
        setDailyClaimStatus('available');
      } else {
        // Default to available if status is unclear
        console.warn('Unexpected claim status:', claimStatus);
        setDailyClaimStatus('available');
      }
    } catch (error) {
      console.error('Failed to check daily claim status:', error);
      // Default to available on error
      setDailyClaimStatus('available');
    }
  };

  const handleClaimDaily = async () => {
    console.log('🎁 Claim button clicked - starting claim process...');
    setDailyClaimStatus('claiming');

    try {
      const claimResult = await claimDailyTokens();
      console.log('📊 Claim API response:', claimResult);

      if (claimResult.success && claimResult.tokensEarned > 0) {
        console.log('✅ Claim successful! Earned:', claimResult.tokensEarned);

        // Force sync with server balance (server already added tokens)
        console.log('🔄 Syncing balance with server...');
        await syncServerBalance();

        // Update local session to reflect new balance
        updateSession();

        setDailyClaimStatus('claimed');
        console.log('✅ Status set to claimed');
      } else if (claimResult.error) {
        console.log('❌ Claim error:', claimResult.error, '-', claimResult.message);

        // Set status based on error type
        if (claimResult.error === 'VPN detected') {
          setDailyClaimStatus('vpn_blocked');
        } else if (claimResult.error === 'Already claimed') {
          setDailyClaimStatus('claimed');
          // Still sync balance in case it was claimed but balance not updated
          await syncServerBalance();
        } else {
          setDailyClaimStatus('available');
        }
      } else {
        // Unexpected response - log and reset
        console.warn('⚠️ Unexpected claim response:', claimResult);
        setDailyClaimStatus('available');
      }
    } catch (error) {
      console.error('💥 Failed to claim daily tokens:', error);
      setDailyClaimStatus('available');
    }
  };

  const handlePaymentRedirect = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');

    if (paymentStatus === 'success' && sessionId) {
      // Verify payment with backend
      const result = await verifyPayment(sessionId);

      if (result.success) {
        // Sync with server (tokens already added server-side)
        await syncServerBalance();
        setPaymentMessage(`🎉 Success! ${result.tokens} Lit-Bits added to your account!`);
        setCurrentView('buy');
      } else {
        setPaymentMessage('❌ Payment verification failed. Please contact support.');
      }

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Clear message after 10 seconds
      setTimeout(() => setPaymentMessage(''), 10000);
    } else if (paymentStatus === 'cancelled') {
      setPaymentMessage('Payment cancelled. No charges were made.');
      setCurrentView('buy');

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Clear message after 5 seconds
      setTimeout(() => setPaymentMessage(''), 5000);
    }
  };

  const fetchTop3Avatars = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/top-3-avatars`);
      if (response.ok) {
        const data = await response.json();
        setTopAvatars(data);
      }
    } catch (error) {
      console.error('Failed to fetch top 3 avatars:', error);
    }
  };

  const updateSession = () => {
    const sessionData = getSession();
    console.log('📊 [App] Updating session state with:', sessionData?.tokens, 'Lit-Bits');
    // Force new object reference to trigger React re-render
    setSession({ ...sessionData });
  };

  const handleNavigate = (view, username = null) => {
    setCurrentView(view);
    if (username) {
      setSelectedUsername(username);
    }
    setMenuOpen(false); // Close menu on navigation
  };

  // Handle authentication routes
  if (window.location.pathname === '/verify-email') {
    return <EmailVerification />;
  }

  if (window.location.pathname === '/reset-password') {
    return <ResetPasswordForm />;
  }

  if (window.location.pathname === '/link-roblox') {
    return <RobloxLinkingPage />;
  }

  if (window.location.pathname === '/link-roblox-callback') {
    return <RobloxLinkCallback />;
  }

  // Old OAuth callback (deprecated, will be removed)
  if (window.location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }

  // Redirect logged-in users without Roblox to linking page
  if (!loading && user && !user.robloxLinked && window.location.pathname !== '/link-roblox' && window.location.pathname !== '/link-roblox-callback') {
    window.location.href = '/link-roblox';
    return null;
  }

  return (
    <div className="app">
      {/* Login Modal */}
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      {/* Daily Free Banner */}
      <div className="test-banner">
        {dailyClaimStatus === 'checking' && (
          <>🎁 Checking daily reward status...</>
        )}
        {dailyClaimStatus === 'claiming' && (
          <>⏳ Claiming your daily reward...</>
        )}
        {dailyClaimStatus === 'claimed' && (
          <>✅ Daily Reward Claimed! Next reward in: {timeUntilReset}</>
        )}
        {dailyClaimStatus === 'vpn_blocked' && (
          <>🚫 VPN Detected! Daily reward blocked. Next reset: {timeUntilReset}</>
        )}
        {dailyClaimStatus === 'available' && (
          <>
            <button
              onClick={handleClaimDaily}
              disabled={false}
              className="claim-daily-button"
              style={{
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '5px',
                color: '#000',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '0.9rem',
                boxShadow: '0 2px 8px rgba(255, 215, 0, 0.3)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(255, 215, 0, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(255, 215, 0, 0.3)';
              }}
            >
              🎁 Claim 50 Free Lit-Bits!
            </button>
            <span style={{marginLeft: '0.5rem', fontSize: '0.85rem', color: '#000'}}>Next reset: {timeUntilReset}</span>
          </>
        )}
      </div>


      {/* Hamburger Menu Button */}
      <button className="hamburger-btn" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
        <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
        <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
        <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
      </button>

      {/* Live Balance Purse - Opposite side from menu */}
      <div className="balance-purse">
        <div className="balance-purse-inner">
          <img src="/litbitcoin.png" alt="Lit-Bit" className="balance-purse-icon" />
          <span className="balance-purse-amount">{liveBalance}</span>
        </div>
      </div>

      {/* Side Navigation Menu */}
      <nav className={`side-nav ${menuOpen ? 'open' : ''}`}>
        <div className="side-nav-header">
          <h2>Menu</h2>
        </div>

        <div className="nav-section">
          <h3 className="nav-section-title">Core</h3>
          <button onClick={() => handleNavigate('landing')} className="nav-btn">
            🏠 Home
          </button>
        </div>

        {/* My Profile - Show only when logged in */}
        {!loading && user && (
          <div className="nav-section">
            <h3 className="nav-section-title">Profile</h3>
            <button onClick={() => handleNavigate('dashboard')} className="nav-btn dashboard-nav">
              📊 Dashboard
            </button>
            <button onClick={() => handleNavigate('profile', user.username)} className="nav-btn">
              👤 My Profile
            </button>
            <button onClick={() => { logout(); setMenuOpen(false); }} className="nav-btn logout-btn">
              🚪 Logout
            </button>
          </div>
        )}

        <div className="nav-section">
          <h3 className="nav-section-title">Participate</h3>
          <button onClick={() => handleNavigate('vote')} className="nav-btn">
            🗳️ Vote (5<img src="/litbitcoin.png" alt="LB" className="litbit-icon-nav" />)
          </button>
          <button onClick={() => handleNavigate('aura')} className="nav-btn aura-btn">
            ⚡ Aura Farm (FREE)
          </button>
        </div>

        <div className="nav-section">
          <h3 className="nav-section-title">Discover</h3>
          <button onClick={() => handleNavigate('leaderboard')} className="nav-btn">
            🏆 Leaderboard
          </button>
          <button onClick={() => handleNavigate('spotlight')} className="nav-btn spotlight-nav">
            ⭐ Spotlight
          </button>
          <button onClick={() => handleNavigate('profile')} className="nav-btn profile-nav">
            🔍 Search Users
          </button>
        </div>

        <div className="nav-section">
          <h3 className="nav-section-title">Shop</h3>
          <button onClick={() => handleNavigate('buy')} className="nav-btn buy-votes-nav">
            💰 Buy Lit-Bits ({session?.tokens || 0})
          </button>
          <button onClick={() => handleNavigate('boosts')} className="nav-btn boost-nav">
            ⚡ Power-Ups
          </button>
        </div>
      </nav>

      {/* Overlay for closing menu */}
      {menuOpen && <div className="menu-overlay" onClick={() => setMenuOpen(false)}></div>}

      {/* Main Content */}
      <main className="main-content">
        {/* Landing Page - Public */}
        {currentView === 'landing' && (
          <Landing
            topAvatars={topAvatars}
            onNavigate={handleNavigate}
            onOpenLogin={() => setShowLoginModal(true)}
          />
        )}

        {/* All other views require authentication */}
        {!loading && !user && currentView !== 'landing' && (
          <div className="login-wall">
            <div className="login-wall-content">
              <h2>🔒 Login Required</h2>
              <p>You need to create an account to access this feature.</p>
              <p>After signing up, you'll link your Roblox character to participate!</p>
              <button onClick={() => setShowLoginModal(true)} className="login-wall-btn">
                Login / Sign Up
              </button>
            </div>
          </div>
        )}

        {/* Protected Views - Only show if logged in */}
        {!loading && user && (
          <>
            {currentView === 'dashboard' && (
              <Dashboard
                onClose={() => setCurrentView('landing')}
                onNavigate={(view, username) => handleNavigate(view, username)}
              />
            )}
            {currentView === 'vote' && (
              <VotingGallery
                session={session}
                onVoteComplete={syncServerBalance}
                onNavigate={handleNavigate}
              />
            )}
            {currentView === 'aura' && (
              <AuraFarming
                session={session}
                onVoteComplete={syncServerBalance}
                onNavigate={handleNavigate}
              />
            )}
            {currentView === 'leaderboard' && (
              <Leaderboard onNavigate={handleNavigate} />
            )}
            {currentView === 'spotlight' && (
              <SpotlightFeed onNavigate={handleNavigate} />
            )}
            {currentView === 'profile' && (
              <UserProfile
                onBack={() => setCurrentView('landing')}
                initialUsername={selectedUsername}
                onVoteComplete={syncServerBalance}
              />
            )}
            {currentView === 'buy' && (
              <>
                {paymentMessage && (
                  <div className={`payment-message ${paymentMessage.includes('Success') ? 'success' : paymentMessage.includes('cancelled') ? 'info' : 'error'}`}>
                    {paymentMessage}
                  </div>
                )}
                <BuyVotes
                  session={session}
                  onPurchaseComplete={updateSession}
                />
              </>
            )}
            {currentView === 'boosts' && (
              <BoostShop
                session={session}
                onPurchaseComplete={syncServerBalance}
              />
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="footer">
        <p>&copy; 2025 Drip Battle. All rights reserved.</p>
        <p className="footer-tagline">Where Roblox Drip Gets Judged 🔥</p>
        <p style={{marginTop: '1rem'}}>
          <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{color: '#00FFFF', textDecoration: 'underline'}}>
            Terms of Service & Token System
          </a>
        </p>
      </footer>
    </div>
  );
}

export default App;
